import {
  LEGACY_MOUNT_ID,
  MSPL_MOUNT_ID,
  NOOP_MOUNT_DECISION_EMITTER_V1,
  SURFACE_MOUNT_V1_ACTIVE_SURFACES,
  SURFACE_MOUNT_V1_ATTRIBUTES,
  SURFACE_MOUNT_V1_CANDIDATE_SELECTORS,
  SURFACE_MOUNT_V1_CONTRACT_VERSION,
  SURFACE_MOUNT_V1_HOOKS,
  SURFACE_MOUNT_V1_RESERVED_SURFACES,
  type MountDecisionCodeV1,
  type MountDecisionEmitterV1,
  type MountDecisionEventV1,
  type MountPathFamilyV1,
  type SurfaceMountV1ActiveSurface,
} from '@find-my-part/types';

/** The distinct-element query pinned by ADR-028 D-1a. */
export const SURFACE_MOUNT_V1_CANDIDATE_SELECTOR = SURFACE_MOUNT_V1_CANDIDATE_SELECTORS.join(', ');

export {
  MOUNT_DECISION_CODES_V1,
  NOOP_MOUNT_DECISION_EMITTER_V1,
  type MountDecisionCodeV1,
  type MountDecisionEmitterV1,
  type MountDecisionEventV1,
  type MountDecisionIdentityV1,
  type MountPathFamilyV1,
} from '@find-my-part/types';

export type MountDecisionDiagnosticV1 =
  | 'MULTIPLE_MOUNT_CANDIDATES'
  | 'MALFORMED_SURFACE_MOUNT'
  | 'RESERVED_SURFACE_INACTIVE'
  | 'FALLBACK_CONTAINER_MISSING'
  | 'QUESTION_ACTIONS_STATIC_QUESTION_CARDINALITY'
  | 'QUESTION_ACTIONS_STATIC_QUESTION_IDENTITY'
  | 'QUESTION_ACTIONS_GALLERY_CARDINALITY'
  | 'QUESTION_ACTIONS_ACCEPTED_ANSWER_CARDINALITY'
  | 'QUESTION_ACTIONS_SLOT_CARDINALITY'
  | 'QUESTION_ACTIONS_SLOT_IDENTITY'
  | 'QUESTION_ACTIONS_STATIC_QUESTION_ANCESTRY'
  | 'QUESTION_ACTIONS_GALLERY_ANCESTRY'
  | 'QUESTION_ACTIONS_ACCEPTED_ANSWER_ANCESTRY'
  | 'QUESTION_ACTIONS_SLOT_ANCESTRY';

export interface QuestionActionsHookAttestationV1 {
  readonly staticQuestion: HTMLElement;
  readonly gallery: Element | null;
  readonly acceptedAnswer: Element | null;
  readonly questionActionsSlot: HTMLElement;
}

export interface ResolvedMountPoint {
  /** Compatibility alias for the persistent outer carrier. */
  readonly element: HTMLElement;
  /** Stable outer mount that owns the token scope and surface attributes. */
  readonly carrier: HTMLElement;
  /** Future React target. For question-actions this is attested now but unused until Slice 2B. */
  readonly renderTarget: HTMLElement;
  readonly mountId: string;
  readonly source: 'mspl-mount' | 'legacy-mount' | 'fallback-created';
  readonly surface: SurfaceMountV1ActiveSurface | null;
  readonly questionId: string | null;
  readonly routingStrategy: 'hash' | 'path';
  readonly hooks: QuestionActionsHookAttestationV1 | null;
}

export interface SurfaceMountResolutionV1 {
  readonly mount: ResolvedMountPoint | null;
  readonly event: MountDecisionEventV1;
  /** null means the bootstrap-level index gate deliberately skipped enumeration. */
  readonly candidateCount: number | null;
  readonly diagnostic: MountDecisionDiagnosticV1 | null;
}

export interface ResolveSurfaceMountV1Options {
  readonly pathname?: string;
  readonly createFallback?: boolean;
  readonly emitter?: MountDecisionEmitterV1;
}

const SURFACE_MOUNT_V1_BASE_PATH = '/find-my-part';
const SURFACE_MOUNT_V1_QUESTIONS_PATH = `${SURFACE_MOUNT_V1_BASE_PATH}/questions`;
const ACTIVE_SURFACES = new Set<string>(SURFACE_MOUNT_V1_ACTIVE_SURFACES);
const RESERVED_SURFACES = new Set<string>(SURFACE_MOUNT_V1_RESERVED_SURFACES);

function normalizeOptional(value: string | null | undefined): string | null {
  const normalized = value?.trim() ?? '';
  return normalized.length > 0 ? normalized : null;
}

function normalizePathname(pathname: string): string {
  const withoutQueryOrHash = pathname.split(/[?#]/, 1)[0] ?? '/';
  const rooted = withoutQueryOrHash.startsWith('/') ? withoutQueryOrHash : `/${withoutQueryOrHash}`;
  const withoutTrailingSlash = rooted.replace(/\/+$/, '');
  return withoutTrailingSlash.length > 0 ? withoutTrailingSlash : '/';
}

/** Classifies paths without retaining a URL, slug, query, or fragment in telemetry. */
export function classifyMountPathFamilyV1(pathname: string): MountPathFamilyV1 {
  const path = normalizePathname(pathname);
  if (path === SURFACE_MOUNT_V1_BASE_PATH) return 'hub';

  if (
    path === SURFACE_MOUNT_V1_QUESTIONS_PATH ||
    /^\/find-my-part\/questions\/page\/[1-9]\d*$/.test(path)
  ) {
    return 'index';
  }
  if (/^\/find-my-part\/questions\/[^/]+$/.test(path)) return 'detail';
  return 'other';
}

function asHtmlElement(element: Element): HTMLElement | null {
  const ownerConstructor = element.ownerDocument.defaultView?.HTMLElement;
  const htmlElementConstructor =
    ownerConstructor ?? (typeof HTMLElement === 'undefined' ? null : HTMLElement);
  return htmlElementConstructor && element instanceof htmlElementConstructor
    ? (element as HTMLElement)
    : null;
}

function routingStrategy(element: Element): 'hash' | 'path' {
  const value = normalizeOptional(
    element.getAttribute(SURFACE_MOUNT_V1_ATTRIBUTES.routingStrategy)
  );
  return value === 'path' ? 'path' : 'hash';
}

// SURFACE_COST_QUESTION_ACTIONS_START
interface QuestionActionsHookResolutionV1 {
  readonly hooks: QuestionActionsHookAttestationV1 | null;
  readonly diagnostic: MountDecisionDiagnosticV1 | null;
}

function exactAttributeSelector(attributeName: string, value: string): string {
  return `[${attributeName}="${value}"]`;
}

/**
 * Attests the renderer-owned detail topology before bootstrap may mutate DOM.
 * Counts are document-wide so a duplicate cannot hide outside the static article.
 */
function resolveQuestionActionsHooksV1(
  doc: Document,
  carrier: HTMLElement,
  questionId: string
): QuestionActionsHookResolutionV1 {
  const staticQuestions = [
    ...doc.querySelectorAll<Element>(
      exactAttributeSelector(
        SURFACE_MOUNT_V1_HOOKS.staticQuestion.attributeName,
        SURFACE_MOUNT_V1_HOOKS.staticQuestion.value
      )
    ),
  ];
  if (staticQuestions.length !== 1) {
    return { hooks: null, diagnostic: 'QUESTION_ACTIONS_STATIC_QUESTION_CARDINALITY' };
  }
  const staticQuestion = asHtmlElement(staticQuestions[0]);
  if (!staticQuestion || staticQuestion.tagName.toLowerCase() !== 'article') {
    return { hooks: null, diagnostic: 'QUESTION_ACTIONS_STATIC_QUESTION_CARDINALITY' };
  }
  if (
    normalizeOptional(staticQuestion.getAttribute(SURFACE_MOUNT_V1_ATTRIBUTES.questionId)) !==
    questionId
  ) {
    return { hooks: null, diagnostic: 'QUESTION_ACTIONS_STATIC_QUESTION_IDENTITY' };
  }

  const galleries = [
    ...doc.querySelectorAll<Element>(
      exactAttributeSelector(
        SURFACE_MOUNT_V1_HOOKS.gallery.attributeName,
        SURFACE_MOUNT_V1_HOOKS.gallery.value
      )
    ),
  ];
  if (galleries.length > 1) {
    return { hooks: null, diagnostic: 'QUESTION_ACTIONS_GALLERY_CARDINALITY' };
  }

  const acceptedAnswers = [
    ...doc.querySelectorAll<Element>(
      exactAttributeSelector(
        SURFACE_MOUNT_V1_HOOKS.acceptedAnswer.attributeName,
        SURFACE_MOUNT_V1_HOOKS.acceptedAnswer.value
      )
    ),
  ];
  if (acceptedAnswers.length > 1) {
    return { hooks: null, diagnostic: 'QUESTION_ACTIONS_ACCEPTED_ANSWER_CARDINALITY' };
  }

  const slots = [
    ...doc.querySelectorAll<Element>(
      exactAttributeSelector(
        SURFACE_MOUNT_V1_HOOKS.questionActionsSlot.attributeName,
        SURFACE_MOUNT_V1_HOOKS.questionActionsSlot.value
      )
    ),
  ];
  if (slots.length !== 1) {
    return { hooks: null, diagnostic: 'QUESTION_ACTIONS_SLOT_CARDINALITY' };
  }
  const questionActionsSlot = asHtmlElement(slots[0]);
  if (!questionActionsSlot || questionActionsSlot.parentElement !== carrier) {
    return { hooks: null, diagnostic: 'QUESTION_ACTIONS_SLOT_ANCESTRY' };
  }
  if (
    normalizeOptional(questionActionsSlot.getAttribute(SURFACE_MOUNT_V1_ATTRIBUTES.questionId)) !==
    null
  ) {
    return { hooks: null, diagnostic: 'QUESTION_ACTIONS_SLOT_IDENTITY' };
  }
  if (staticQuestion === carrier || !staticQuestion.contains(carrier)) {
    return { hooks: null, diagnostic: 'QUESTION_ACTIONS_STATIC_QUESTION_ANCESTRY' };
  }

  const gallery = galleries[0] ?? null;
  if (gallery && !staticQuestion.contains(gallery)) {
    return { hooks: null, diagnostic: 'QUESTION_ACTIONS_GALLERY_ANCESTRY' };
  }
  const acceptedAnswer = acceptedAnswers[0] ?? null;
  if (acceptedAnswer && !staticQuestion.contains(acceptedAnswer)) {
    return { hooks: null, diagnostic: 'QUESTION_ACTIONS_ACCEPTED_ANSWER_ANCESTRY' };
  }

  return {
    hooks: { staticQuestion, gallery, acceptedAnswer, questionActionsSlot },
    diagnostic: null,
  };
}
// SURFACE_COST_QUESTION_ACTIONS_END

export function createMountDecisionEventV1(
  decision: MountDecisionCodeV1,
  pathFamily: MountPathFamilyV1
): MountDecisionEventV1 {
  return {
    eventVersion: '1',
    decision,
    pathFamily,
    // UXR-006A has no provenance-validated identity source. Syntax alone cannot
    // prove that page/script input is non-PII, so -006B must bind trusted IDs.
    tenant: { kind: 'unavailable' },
    deployment: { kind: 'unavailable' },
  };
}

function finish(
  mount: ResolvedMountPoint | null,
  decision: MountDecisionCodeV1,
  pathFamily: MountPathFamilyV1,
  candidateCount: number | null,
  diagnostic: MountDecisionDiagnosticV1 | null,
  options: ResolveSurfaceMountV1Options
): SurfaceMountResolutionV1 {
  const event = createMountDecisionEventV1(decision, pathFamily);
  try {
    (options.emitter ?? NOOP_MOUNT_DECISION_EMITTER_V1).emit(event);
  } catch {
    // Decision telemetry is observational. A future UXR-006B transport must
    // report its own delivery health; it can never take down storefront mount
    // resolution or turn a valid decision into a thrown bootstrap.
  }
  return { mount, event, candidateCount, diagnostic };
}

/**
 * Total SurfaceMountV1 decision grammar.
 *
 * Index exclusion is rule 0b at bootstrap level and therefore runs before the
 * candidate query. Everywhere else the comma-union query plus an explicit Set
 * guarantees one DOM element matching several patterns is counted once.
 */
export function resolveSurfaceMountV1(
  doc: Document,
  options: ResolveSurfaceMountV1Options = {}
): SurfaceMountResolutionV1 {
  const pathname = options.pathname ?? SURFACE_MOUNT_V1_BASE_PATH;
  const pathFamily = classifyMountPathFamilyV1(pathname);

  if (pathFamily === 'index') {
    return finish(null, 'none-excluded', pathFamily, null, null, options);
  }

  const candidates = [
    ...new Set(doc.querySelectorAll<Element>(SURFACE_MOUNT_V1_CANDIDATE_SELECTOR)),
  ];

  if (candidates.length > 1) {
    return finish(
      null,
      'multiple',
      pathFamily,
      candidates.length,
      'MULTIPLE_MOUNT_CANDIDATES',
      options
    );
  }

  if (candidates.length === 0) {
    if (options.createFallback !== false) {
      const container = doc.querySelector('.page-content, main, #main-content, .body');
      const htmlContainer = container ? asHtmlElement(container) : null;
      if (htmlContainer) {
        const element = doc.createElement('div');
        element.id = MSPL_MOUNT_ID;
        htmlContainer.appendChild(element);
        return finish(
          {
            element,
            carrier: element,
            renderTarget: element,
            mountId: MSPL_MOUNT_ID,
            source: 'fallback-created',
            surface: null,
            questionId: null,
            routingStrategy: 'hash',
            hooks: null,
          },
          'fallback-created',
          pathFamily,
          0,
          null,
          options
        );
      }
    }
    return finish(
      null,
      'none-excluded',
      pathFamily,
      0,
      options.createFallback === false ? null : 'FALLBACK_CONTAINER_MISSING',
      options
    );
  }

  const candidate = candidates[0];
  const element = asHtmlElement(candidate);
  const surface = normalizeOptional(candidate.getAttribute(SURFACE_MOUNT_V1_ATTRIBUTES.surface));
  const version = normalizeOptional(
    candidate.getAttribute(SURFACE_MOUNT_V1_ATTRIBUTES.contractVersion)
  );
  const questionId = normalizeOptional(
    candidate.getAttribute(SURFACE_MOUNT_V1_ATTRIBUTES.questionId)
  );
  const carriesContract = surface !== null || version !== null;

  if (!carriesContract) {
    const recognizedId = candidate.id === MSPL_MOUNT_ID || candidate.id === LEGACY_MOUNT_ID;
    if (!element || !recognizedId) {
      return finish(null, 'malformed', pathFamily, 1, 'MALFORMED_SURFACE_MOUNT', options);
    }
    return finish(
      {
        element,
        carrier: element,
        renderTarget: element,
        mountId: candidate.id,
        source: candidate.id === MSPL_MOUNT_ID ? 'mspl-mount' : 'legacy-mount',
        surface: null,
        questionId,
        routingStrategy: routingStrategy(candidate),
        hooks: null,
      },
      'old-id',
      pathFamily,
      1,
      null,
      options
    );
  }

  const reserved = surface !== null && RESERVED_SURFACES.has(surface);
  const hasExpectedIdentity =
    surface === 'question-actions' ? questionId !== null : questionId === null;
  const isValidContract =
    element !== null &&
    candidate.id === MSPL_MOUNT_ID &&
    version === SURFACE_MOUNT_V1_CONTRACT_VERSION &&
    surface !== null &&
    ACTIVE_SURFACES.has(surface) &&
    hasExpectedIdentity;

  if (!isValidContract || !element) {
    return finish(
      null,
      'malformed',
      pathFamily,
      1,
      reserved ? 'RESERVED_SURFACE_INACTIVE' : 'MALFORMED_SURFACE_MOUNT',
      options
    );
  }

  const hookResolution =
    surface === 'question-actions'
      ? resolveQuestionActionsHooksV1(doc, element, questionId as string)
      : { hooks: null, diagnostic: null };
  if (hookResolution.diagnostic) {
    return finish(null, 'malformed', pathFamily, 1, hookResolution.diagnostic, options);
  }
  const renderTarget = hookResolution.hooks?.questionActionsSlot ?? element;

  return finish(
    {
      element,
      carrier: element,
      renderTarget,
      mountId: MSPL_MOUNT_ID,
      source: 'mspl-mount',
      surface: surface as SurfaceMountV1ActiveSurface,
      questionId,
      routingStrategy: routingStrategy(candidate),
      hooks: hookResolution.hooks,
    },
    'contract',
    pathFamily,
    1,
    null,
    options
  );
}

/** Compatibility wrapper retained for existing call sites and tests. */
export function findOrCreateProductionMountPoint(
  doc: Document,
  options: ResolveSurfaceMountV1Options = {}
): ResolvedMountPoint | null {
  return resolveSurfaceMountV1(doc, options).mount;
}
